import { IFinding, IValidationInput, IValidationOutput, IValidator } from "../types";
import { isFiniteNumber, isIsoBayKey, isPlainObject, isRowKey, isYesNo } from "./patterns";

import { createValidationOutput } from "../helpers/createValidationOutput";

const name = "lidStructureValidator";
const okTranslationKey = "validators:structure.lidStructure.ok";
const errorTranslationKey = "validators:structure.lidStructure.error";

export type TLidStructureCode =
  | "missing"
  | "wrongType"
  | "badPattern"
  | "badEnum"
  | "outOfBounds"
  /** `startIsoBay` is after `endIsoBay` */
  | "inverted";

export interface ILidStructureFinding extends IFinding {
  code: TLidStructureCode;
  /** Relative to the lid: `label`, `portIsoRow`, … `""` for the lid itself */
  path: string;
  index: number;
  label?: string;
}

export interface ILidStructureOutput {
  findings: ILidStructureFinding[];
}

/**
 * The shape of every lid: its label, its two rows, its two bays (odd, in bounds and in
 * order), the overlap flags and the weight. Whether the rows and bays exist in the vessel
 * is a data check, not a structure one. Never throws.
 */
export const lidStructureValidator: IValidator<ILidStructureOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: lidStructureValidatorFn,
  severity: "error",
  stage: "structure",
};

export function lidStructureValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<ILidStructureOutput> {
  const findings: ILidStructureFinding[] = [];
  const lidData: unknown[] = Array.isArray(json?.lidData) ? json.lidData : [];

  lidData.forEach((lid, index) => {
    if (!isPlainObject(lid)) {
      findings.push({ code: "wrongType", path: "", index });
      return;
    }

    const label = typeof lid["label"] === "string" ? lid["label"] : undefined;
    const flag = (code: TLidStructureCode, path: string) =>
      findings.push({ code, path, index, label });

    if (lid["label"] === undefined) flag("missing", "label");
    else if (typeof lid["label"] !== "string") flag("wrongType", "label");

    (["portIsoRow", "starboardIsoRow"] as const).forEach((f) => {
      if (lid[f] === undefined) flag("missing", f);
      else if (!isRowKey(lid[f])) flag("badPattern", f);
    });

    (["startIsoBay", "endIsoBay"] as const).forEach((f) => {
      if (lid[f] === undefined) flag("missing", f);
      else if (!isIsoBayKey(lid[f])) flag("badPattern", f);
    });

    if (
      isIsoBayKey(lid["startIsoBay"]) &&
      isIsoBayKey(lid["endIsoBay"]) &&
      Number(lid["startIsoBay"]) > Number(lid["endIsoBay"])
    )
      flag("inverted", "startIsoBay");

    (["overlapPort", "overlapStarboard"] as const).forEach((f) => {
      if (lid[f] !== undefined && !isYesNo(lid[f])) flag("badEnum", f);
    });

    if (lid["weight"] !== undefined) {
      if (!isFiniteNumber(lid["weight"])) flag("wrongType", "weight");
      else if (lid["weight"] < 0) flag("outOfBounds", "weight");
    }
  });

  return createValidationOutput(lidStructureValidator, findings, { findings });
}
